<template>
  <div class="scoring-standard-management">
    <el-card class="scoring-standard-card">
      <div slot="header" class="card-header">
        <span>评分标准管理</span>
        <el-button type="primary" class="add-standard-btn" @click="showAddStandardDialog">添加标准</el-button>
      </div>

      <!-- 筛选区域 -->
      <div class="filter-container">
        <el-row :gutter="20">
          <el-col :span="8">
            <el-select v-model="filterForm.testItemId" placeholder="选择体测项目">
              <el-option value="" label="全部项目"></el-option>
              <el-option v-for="item in testItems" :key="item.id" :value="item.id">
                {{ item.name }}
              </el-option>
            </el-select>
          </el-col>
          <el-col :span="8">
            <el-select v-model="filterForm.gender" placeholder="选择性别">
              <el-option value="" label="全部性别"></el-option>
              <el-option value="MALE" label="男"></el-option>
              <el-option value="FEMALE" label="女"></el-option>
            </el-select>
          </el-col>
          <el-col :span="8">
            <el-button type="primary" @click="fetchStandards">查询</el-button>
            <el-button @click="resetFilter">重置</el-button>
          </el-col>
        </el-row>
      </div>

      <!-- 评分标准表格 -->
      <el-table
        v-loading="loading"
        :data="standards"
        border
        style="width: 100%"
        class="standards-table"
      >
        <el-table-column type="index" label="序号" width="50"></el-table-column>
        <el-table-column prop="testItemName" label="体测项目" width="150"></el-table-column>
        <el-table-column prop="gender" label="性别" width="80">
          <template #default="scope">
            {{ scope.row.gender === 'MALE' ? '男' : '女' }}
          </template>
        </el-table-column>
        <el-table-column prop="minScore" label="最低分数" width="100"></el-table-column>
        <el-table-column prop="maxScore" label="最高分数" width="100"></el-table-column>
        <el-table-column prop="minValue" label="最低值" width="100"></el-table-column>
        <el-table-column prop="maxValue" label="最高值" width="100"></el-table-column>
        <el-table-column prop="scoringFormula" label="评分公式" width="180"></el-table-column>
        <el-table-column label="操作" width="180">
          <template #default="scope">
            <el-button type="text" @click="showEditStandardDialog(scope.row)">编辑</el-button>
            <el-button type="text" danger @click="deleteStandard(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50]"
          :page-size="pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 添加/编辑评分标准对话框 -->
    <el-dialog v-model="standardDialogVisible" :title="isEdit ? '编辑评分标准' : '添加评分标准'" width="700px">
      <el-form
        ref="standardForm"
        :model="standardForm"
        :rules="standardRules"
        label-width="120px"
      >
        <el-form-item label="体测项目" prop="testItemId">
          <el-select v-model="standardForm.testItemId" placeholder="请选择体测项目">
            <el-option v-for="item in testItems" :key="item.id" :value="item.id">
              {{ item.name }}
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="性别" prop="gender">
          <el-select v-model="standardForm.gender" placeholder="请选择性别">
            <el-option value="MALE" label="男"></el-option>
            <el-option value="FEMALE" label="女"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="年龄范围" prop="ageRange">
          <el-input v-model="standardForm.ageRange" placeholder="例如: 18-22"></el-input>
        </el-form-item>
        <el-form-item label="最低分数" prop="minScore">
          <el-input v-model.number="standardForm.minScore" type="number" placeholder="请输入最低分数"></el-input>
        </el-form-item>
        <el-form-item label="最高分数" prop="maxScore">
          <el-input v-model.number="standardForm.maxScore" type="number" placeholder="请输入最高分数"></el-input>
        </el-form-item>
        <el-form-item label="最低值" prop="minValue">
          <el-input v-model.number="standardForm.minValue" type="number" placeholder="请输入最低值"></el-input>
        </el-form-item>
        <el-form-item label="最高值" prop="maxValue">
          <el-input v-model.number="standardForm.maxValue" type="number" placeholder="请输入最高值"></el-input>
        </el-form-item>
        <el-form-item label="评分公式" prop="scoringFormula">
          <el-input v-model="standardForm.scoringFormula" placeholder="例如: (value - minValue) * (maxScore - minScore) / (maxValue - minValue) + minScore"></el-input>
          <el-tooltip class="item" effect="dark" content="使用value代表实际测量值，minValue、maxValue、minScore、maxScore代表上述参数" placement="top">
            <el-icon class="info-icon"><QuestionFilled /></el-icon>
          </el-tooltip>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="standardDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitStandardForm">提交</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, getCurrentInstance } from 'vue';
import { ElMessage, ElMessageBox, ElIcon } from 'element-plus';
import { QuestionFilled } from '@element-plus/icons-vue';
import axios from 'axios';

const { proxy } = getCurrentInstance();

// 加载状态
const loading = ref(false);

// 体测项目列表
const testItems = ref([]);

// 筛选条件
const filterForm = reactive({
  testItemId: '',
  gender: ''
});

// 表格数据
const standards = ref([]);
const total = ref(0);
const currentPage = ref(1);
const pageSize = ref(10);

// 对话框状态
const standardDialogVisible = ref(false);
const isEdit = ref(false);

// 评分标准表单
const standardForm = reactive({
  id: '',
  testItemId: '',
  gender: 'MALE',
  ageRange: '',
  minScore: 0,
  maxScore: 100,
  minValue: 0,
  maxValue: 0,
  scoringFormula: ''
});

// 评分标准表单验证规则
const standardRules = {
  testItemId: [
    { required: true, message: '请选择体测项目', trigger: 'change' }
  ],
  gender: [
    { required: true, message: '请选择性别', trigger: 'change' }
  ],
  ageRange: [
    { required: true, message: '请输入年龄范围', trigger: 'blur' },
    { pattern: /^\d+-\d+$/, message: '请输入正确的年龄范围格式，例如: 18-22', trigger: 'blur' }
  ],
  minScore: [
    { required: true, message: '请输入最低分数', trigger: 'blur' },
    { type: 'number', message: '请输入数字', trigger: 'blur' },
    { min: 0, max: 100, message: '分数范围在 0 到 100 之间', trigger: 'blur' }
  ],
  maxScore: [
    { required: true, message: '请输入最高分数', trigger: 'blur' },
    { type: 'number', message: '请输入数字', trigger: 'blur' },
    { min: 0, max: 100, message: '分数范围在 0 到 100 之间', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (value <= standardForm.minScore) {
          callback(new Error('最高分数必须大于最低分数'));
        } else {
          callback();
        }
      },
      trigger: 'blur'
    }
  ],
  minValue: [
    { required: true, message: '请输入最低值', trigger: 'blur' },
    { type: 'number', message: '请输入数字', trigger: 'blur' }
  ],
  maxValue: [
    { required: true, message: '请输入最高值', trigger: 'blur' },
    { type: 'number', message: '请输入数字', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (value <= standardForm.minValue) {
          callback(new Error('最高值必须大于最低值'));
        } else {
          callback();
        }
      },
      trigger: 'blur'
    }
  ],
  scoringFormula: [
    { required: true, message: '请输入评分公式', trigger: 'blur' },
    { min: 5, message: '评分公式不能少于 5 个字符', trigger: 'blur' }
  ]
};

// 获取体测项目列表
const fetchTestItems = async () => {
  try {
    // 使用后端现有的列表API，设置较大的pageSize以获取所有项目
    const response = await axios.get('/admin/test-items/list', { 
      params: { 
        page: 1, 
        size: 100 // 设置一个较大的值以获取所有项目
      } 
    });
    // 转换数据格式以匹配前端需求
    testItems.value = response.data.data.map(item => ({
      id: item.itemId,
      name: item.itemName,
      unit: item.unit,
      status: item.status
    }));
  } catch (error) {
    console.error('获取体测项目列表失败:', error);
    ElMessage.error('获取体测项目列表失败');
  }
};

// 获取评分标准列表
const fetchStandards = async () => {
  try {
    loading.value = true;
    const params = {
      page: currentPage.value,
      size: pageSize.value,
      testItemId: filterForm.testItemId || undefined,
      gender: filterForm.gender || undefined
    };

    const response = await axios.get('/admin/scoring-standards', { params });
    // 调整为后端返回的数据格式
    if (response.data.code === 200) {
      standards.value = response.data.data;
      total.value = response.data.total;
    } else {
      standards.value = [];
      total.value = 0;
      ElMessage.error(response.data.message || '获取评分标准列表失败');
    }
  } catch (error) {
    console.error('获取评分标准列表失败:', error);
    ElMessage.error('获取评分标准列表失败');
  } finally {
    loading.value = false;
  }
};

// 重置筛选
const resetFilter = () => {
  filterForm.testItemId = '';
  filterForm.gender = '';
  currentPage.value = 1;
  fetchStandards();
};

// 分页处理
const handleSizeChange = (size) => {
  pageSize.value = size;
  currentPage.value = 1;
  fetchStandards();
};

const handleCurrentChange = (page) => {
  currentPage.value = page;
  fetchStandards();
};

// 显示添加评分标准对话框
const showAddStandardDialog = () => {
  isEdit.value = false;
  standardForm.id = '';
  standardForm.testItemId = '';
  standardForm.gender = 'MALE';
  standardForm.ageRange = '';
  standardForm.minScore = 0;
  standardForm.maxScore = 100;
  standardForm.minValue = 0;
  standardForm.maxValue = 0;
  standardForm.scoringFormula = '';
  standardDialogVisible.value = true;
};

// 显示编辑评分标准对话框
const showEditStandardDialog = (standard) => {
  isEdit.value = true;
  standardForm.id = standard.id;
  standardForm.testItemId = standard.testItemId;
  standardForm.gender = standard.gender;
  standardForm.ageRange = standard.ageRange;
  standardForm.minScore = standard.minScore;
  standardForm.maxScore = standard.maxScore;
  standardForm.minValue = standard.minValue;
  standardForm.maxValue = standard.maxValue;
  standardForm.scoringFormula = standard.scoringFormula;
  standardDialogVisible.value = true;
};

// 提交评分标准表单
const submitStandardForm = async () => {
  try {
    const form = proxy.$refs.standardForm;
    await form.validate();

    if (isEdit.value) {
      // 编辑评分标准
      const response = await axios.put(`/admin/scoring-standards/${standardForm.id}`, standardForm);
      if (response.data.code === 200) {
        ElMessage.success('评分标准编辑成功');
      } else {
        ElMessage.error(response.data.message || '评分标准编辑失败');
        return;
      }
    } else {
      // 添加评分标准
      const response = await axios.post('/admin/scoring-standards', standardForm);
      if (response.data.code === 200) {
        ElMessage.success('评分标准添加成功');
      } else {
        ElMessage.error(response.data.message || '评分标准添加失败');
        return;
      }
    }

    standardDialogVisible.value = false;
    fetchStandards();
  } catch (error) {
    console.error('提交评分标准表单失败:', error);
    ElMessage.error(isEdit.value ? '评分标准编辑失败' : '评分标准添加失败');
  }
};

// 删除评分标准
const deleteStandard = async (standard) => {
  try {
    await ElMessageBox.confirm('确定要删除该评分标准吗？', '警告', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    });

    const response = await axios.delete(`/admin/scoring-standards/${standard.id}`);
    if (response.data.code === 200) {
      ElMessage.success('评分标准删除成功');
      fetchStandards();
    } else {
      ElMessage.error(response.data.message || '评分标准删除失败');
    }
  } catch (error) {
    if (error === 'cancel') return;
    console.error('删除评分标准失败:', error);
    ElMessage.error('评分标准删除失败');
  }
};

onMounted(() => {
  fetchTestItems();
  fetchStandards();
});
</script>

<style scoped>
.scoring-standard-management {
  padding: 10px 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.add-standard-btn {
  margin-top: 5px;
}

.filter-container {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.standards-table {
  margin-bottom: 20px;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
}

.info-icon {
  margin-left: 5px;
  color: #409eff;
}
</style>